import { useAuth } from "@clerk/nextjs";
import { OrderStatus } from "@prisma/client";
import { UseQueryResult, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { ManagedEmail } from "~/lib/drizzle/type";
import { apiBaseUrl, fetchWrapper } from "~/utils/utils";
import { BaseProps } from "./base";

interface ConnectEmailPayload {
    provider: 'google' | 'microsoft' | 'imap';
    redirect_url: string;
}

interface AssignEmailPayload {
    id: string;
    user_ids: string[];
}

export const useConnectEmail = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const router = useRouter();

    const mutationFn = async (payload: ConnectEmailPayload) => {
        const token = await getToken();
        const data = await fetchWrapper<{ url: string }>({
            url: `${apiBaseUrl}/emails/authenticate`,
            method: 'POST',
            token,
            body: payload
        });

        if (data?.url) {
            router.push(data.url);
            return;
        }

        return data
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

export const useGetManagedEmails = (): UseQueryResult<ManagedEmail[]> => {
    const { getToken } = useAuth();

    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<ManagedEmail[]>({
            url: `${apiBaseUrl}/emails?limit=1000`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getManagedEmails'],
        queryFn,
    })
}

export const useAssignEmail = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const query = useQueryClient();

    const mutationFn = async (payload: AssignEmailPayload) => {
        const token = await getToken();
        const data = await fetchWrapper<ManagedEmail>({
            url: `${apiBaseUrl}/emails/${payload.id}/assign`,
            method: 'POST',
            token,
            body: {
                user_ids: payload.user_ids
            }
        });

        return data
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            await query.invalidateQueries({
                queryKey: ['getManagedEmails']
            });
            await query.invalidateQueries({
                queryKey: ['getAssignedEmails']
            });
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

export const useGetAssignedEmails = (): UseQueryResult<ManagedEmail[]> => {
    const { getToken } = useAuth();

    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<ManagedEmail[]>({
            url: `${apiBaseUrl}/emails/assigned`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getAssignedEmails'],
        queryFn,
    })
}

interface GetThreadsParams {
    emailId: string;
    keyword?: string;
}

export const useGetThreads = (params: GetThreadsParams) => {
    const { getToken } = useAuth();

    const queryFn = async ({ pageParam = undefined }) => {
        const token = await getToken();
        const urlParams = new URLSearchParams();
        urlParams.append('email_id', params.emailId);
        if (params.keyword) {
            urlParams.append('keyword', params.keyword);
        }
        if (pageParam) {
            urlParams.append('cursor', pageParam);
        }

        const result = await fetchWrapper<GetThreadsResponse>({
            url: `${apiBaseUrl}/emails/threads?${urlParams.toString()}`,
            method: 'GET',
            token
        });

        if (!result) {
            return {
                data: [],
                nextCursor: undefined
            }
        }

        return result;
    }

    return useInfiniteQuery({
        queryKey: ['getThreads', params.emailId, params.keyword],
        queryFn,
        getNextPageParam: (lastPage, _allPages) => lastPage.nextCursor,
        refetchInterval: 1000 * 30, // 30 seconds
        staleTime: 1000 * 60 * 5, // 5 minutes
    })
}

export const useGetEmails = (emailId: string, threadId: string, messageIds: string[]): UseQueryResult<EmailMessage[]> => {
    const { getToken } = useAuth();

    const queryFn = async () => {
        const token = await getToken();
        const urlParams = new URLSearchParams();
        urlParams.append('email_id', emailId);
        messageIds.forEach((id) => {
            urlParams.append('message_ids', id);
        });

        const result = await fetchWrapper<ManagedEmail[]>({
            url: `${apiBaseUrl}/emails/threads/${threadId}/messages?${urlParams.toString()}`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getEmails', threadId, ...messageIds],
        queryFn,
        staleTime: 1000 * 60,
    })
}

type DownloadAttachmentPayload = {
    emailId: string;
    threadId: string;
    messageId: string;
    attachmentId: string;
}

type DownloadAttachmentResponse = {
    fileName: string;
    fileSize: number;
    data: string;
    contentType: string;
}

export const useDownloadAttachment = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();

    const mutationFn = async (payload: DownloadAttachmentPayload) => {
        const token = await getToken();

        const result = await fetchWrapper<DownloadAttachmentResponse>({
            url: `${apiBaseUrl}/emails/threads/${payload.threadId}/messages/attachments`,
            method: 'POST',
            token,
            body: {
                email_id: payload.emailId,
                message_id: payload.messageId,
                attachment_id: payload.attachmentId
            }
        });

        return result
    }

    return useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })
}

type UpdateThreadPayload = {
    emailId: string;
    threadId: string;
    unread: boolean;
}

type UpdateThreadResponse = {
    unread: boolean;
}

export const useUpdateThread = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const mutationFn = async (payload: UpdateThreadPayload) => {
        const token = await getToken();

        const result = await fetchWrapper<UpdateThreadResponse>({
            url: `${apiBaseUrl}/emails/threads/${payload.threadId}`,
            method: 'PATCH',
            token,
            body: {
                email_id: payload.emailId,
                unread: payload.unread
            }
        });

        return result
    }

    return useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            queryClient.setQueryData<InfiniteData<Thread>>(['getThreads', variables.emailId, undefined], (oldData) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => {
                        return {
                            ...page,
                            data: page.data.map((thread) => {
                                if (thread.id === variables.threadId) {
                                    return {
                                        ...thread,
                                        unread: variables.unread
                                    }
                                }

                                return thread;
                            })
                        }
                    })
                };
            });
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })
}

interface InfiniteData<T> {
    pages: {
        data: T[];
    }[];
}

interface ThreadParticipant {
    email: string;
    name?: string;
}

export interface ThreadMessage {
    unread: boolean;
    subject: string;
    from: ThreadParticipant[];
    hasProcessedOrder: boolean;
}
export interface Thread {
    id: string;
    subject: string;
    snippet: string;
    participants: ThreadParticipant[];
    messageIds: string[];
    latestMessageReceivedDate: number;
    latestDraftOrMessage: ThreadMessage;
    unread: boolean;
    hasProcessedOrder: boolean;
}
interface GetThreadsResponse {
    data: Thread[];
    nextCursor?: string;
}

interface EmailMessageContent {
    cc: ThreadParticipant[];
    from: ThreadParticipant[];
    to: ThreadParticipant[];
    bcc: ThreadParticipant[];
    body: string;
    date: number;
    unread: boolean;
    snippet: string;
    subject: string;
    threadId: string;
    attachments?: EmailAttachment[];
}

export interface EmailAttachment {
    id: string;
    size: number;
    grantId: string;
    filename: string;
    isInline: boolean;
    contentId: string;
    contentType: string;
    contentDisposition: string;
}

export interface EmailMessage {
    id: string;
    managed_email_id: string;
    thread_id: string;
    message_id: string;
    content: EmailMessageContent;
    timestamp: Date;
    created_at: Date;
    updated_at: Date;
    orders: {
        id: string;
        po_ref: string;
        status: OrderStatus;
        email_message_attachment_id: string;
        email_thread_id: string;
        managed_email_id: string;
    }[];
}
